import { graphql, SliceComponentProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect } from 'react';

import { Serialize } from '../../helpers/api/interfaces/serialize.interface';
import { isBrowser } from '../../helpers/is-browser';
import useGetMobileAppWebViewHeader from '../../hooks/use-get-mobile-app-web-view';
import { ILibraryMeetingPlace } from '../../queries/fragments/libraryMeetingPlace';
import { ILibraryMenuGroup } from '../../queries/fragments/libraryMenuGroup';
import { IMeetingPlacePageSearchResults } from '../../queries/fragments/meetingPlacePageSearchResults';
import {
  IImageWrapperFile,
  IImageWrapperSvg,
} from '../../queries/fragments/svg';
import { PageContext } from '../../templates/page-context.interface';
import { ITextAndImageCardBlock } from '../blocks/text-and-image-card-block/text-and-image-card-block';
import { Header } from '../header';
import SmartAppBanner from '../smart-app-banner/smart-app-banner';
import { TabBar } from '../tab-bar';
import { Whatsapp } from '../whatsapp-messenger/whatsapp';

type LayoutProps = {
  contentfulMeetingPlacePageHome: {
    whatsappNumber: number;
    hideWhatsapp?: boolean;
    meetingPlace?: ILibraryMeetingPlace;
    menuItem: Array<ILibraryMenuGroup>;
    menuCards: ITextAndImageCardBlock[];
    searchResults?: IMeetingPlacePageSearchResults;
  };
  playStoreImage?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
    svg: IImageWrapperSvg;
    file: IImageWrapperFile;
  };
  appStoreImage?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
    svg: IImageWrapperSvg;
    file: IImageWrapperFile;
  };
};

type AdditionalProps = { pageContext: Serialize<PageContext> };

export const Layout = ({
  children,
  data,
  pageContext,
}: SliceComponentProps<LayoutProps, '', AdditionalProps>) => {
  const meetingPlace = data?.contentfulMeetingPlacePageHome?.meetingPlace;
  const menuCards = data?.contentfulMeetingPlacePageHome?.menuCards;
  const menuItem = data?.contentfulMeetingPlacePageHome?.menuItem;
  const searchResults = data?.contentfulMeetingPlacePageHome?.searchResults;
  const whatsappNumber = data.contentfulMeetingPlacePageHome?.whatsappNumber;
  const hideWhatsapp = pageContext.hideWhatsapp;
  const playStoreImage = data?.playStoreImage;
  const appStoreImage = data?.appStoreImage;

  const isWebView = useGetMobileAppWebViewHeader();

  useEffect(() => {
    // LW-1743: Send event to GTM when user clicks on a link to IKEA
    const sendEventToGtm = (e: Event) => {
      const target = e.target as HTMLLinkElement;

      if (!isBrowser || !target) return;

      let href: string | null = null;

      if (target instanceof HTMLAnchorElement) {
        href = target.href;
      } else {
        const closestAnchor = target.closest(
          '.ikea',
        ) as HTMLAnchorElement | null;
        if (closestAnchor) href = closestAnchor.href;
      }

      if (href) {
        const matches = href.match(/(http|https):\/\/(www\.|)ikea\.(.*)/);
        if (matches && dataLayer) {
          dataLayer.push({
            event: 'click_to_ikea',
          });
        }
      }
    };
    const element = window.document.body;
    element.addEventListener('click', sendEventToGtm);
    return () => {
      element.removeEventListener('click', sendEventToGtm);
    };
  }, []);

  // check for newsletter param to open newsletter side modal
  useEffect(() => {
    if (isBrowser) {
      const windowUrl = window.location.search;
      const params = new URLSearchParams(windowUrl);
      const newsletter = params.get('newsletter');
      if (newsletter === 'true') {
        const newsletterButton = document.getElementById('newsletter-button');
        newsletterButton?.click();
      }
    }
  }, [isBrowser && window.location.search]);

  return (
    <>
      {whatsappNumber && !hideWhatsapp && !isWebView && (
        <Whatsapp whatsappNumber={whatsappNumber} />
      )}
      {meetingPlace && !isWebView && (
        <TabBar
          menuGroups={menuItem}
          menuCards={menuCards}
          logo={meetingPlace?.logo}
          socialMediaLinks={meetingPlace?.socialMediaLinks}
          meetingPlace={meetingPlace}
          node_locale={meetingPlace?.node_locale}
          searchResults={searchResults}
          pageContext={pageContext}
        />
      )}
      {meetingPlace &&
        (process.env.GATSBY_IOS_APP_ID || process.env.GASTBY_ANDROID_APP_ID) &&
        !isWebView && (
          <SmartAppBanner
            title={meetingPlace?.name}
            appStoreImage={appStoreImage}
            playStoreImage={playStoreImage}
            node_locale={meetingPlace.node_locale}
          />
        )}
      <div
        className={`smartbanner-top-margin relative xl:container lg:px-28 xl:px-28 ${
          isWebView ? '!pb-6' : ''
        }`}
      >
        {meetingPlace && !isWebView && (
          <Header meetingPlace={meetingPlace} logo={meetingPlace?.logo} />
        )}
        {children}
      </div>
    </>
  );
};

export default Layout;

export const query = graphql`
  query LayoutSliceQuery(
    $tagId: String!
    $node_locale: String!
    $appStoreImageId: String
    $playStoreImageId: String
  ) {
    contentfulMeetingPlacePageHome(
      metadata: { tags: { elemMatch: { contentful_id: { eq: $tagId } } } }
      node_locale: { eq: $node_locale }
    ) {
      menuItem {
        ...LibraryMenuGroup
      }
      menuCards {
        ...TextAndImageCardBlock
      }
      whatsappNumber
      meetingPlace {
        ... on Node {
          ... on ContentfulLibraryMeetingPlace {
            name
            node_locale
            logo {
              description
              gatsbyImageData(width: 144, formats: AVIF)
              ... on ContentfulAsset {
                ...Svg
              }
            }
            openingHours {
              ... on ContentfulLibraryOpeningHours {
                ...LibraryOpeningHours
              }
            }
            openingHoursExceptions {
              ... on ContentfulLibraryOpeningHoursException {
                ...LibraryOpeningHoursExceptions
              }
            }
            openingHoursLink {
              ...MeetingPlaceLinkToAnyPage
            }
            timezone
            socialMediaLinks {
              ...LibrarySocialMediaLinks
            }
            primaryLanguage {
              ... on ContentfulLibraryLanguage {
                ...LibraryLanguage
              }
            }
            languages {
              ... on ContentfulLibraryLanguage {
                ...LibraryLanguage
              }
            }
            showLanguageSelector
          }
        }
      }
      searchResults {
        ...MeetingPlacePageSearchResults
      }
    }

    appStoreImage: contentfulAsset(
      contentful_id: { eq: $appStoreImageId }
      node_locale: { eq: $node_locale }
    ) {
      gatsbyImageData(width: 140, height: 60, formats: AVIF)
      description
      ... on ContentfulAsset {
        ...Svg
      }
    }
    playStoreImage: contentfulAsset(
      contentful_id: { eq: $playStoreImageId }
      node_locale: { eq: $node_locale }
    ) {
      gatsbyImageData(width: 140, height: 60, formats: AVIF)
      description
      ... on ContentfulAsset {
        ...Svg
      }
    }
  }
`;
